/**
 * Transient (non-reactive) client state.
 *
 * Holds data that must NOT live in the Zustand store: values that update every
 * frame (map marker positions) and would thrash React, an imperative registry
 * (the chunk-clear callback), and the 10 Hz debug-stats rate limiter. These are
 * the only genuinely non-store roles the old StoreBridge served.
 */

import { useGameStore } from './store';

// ============== Map overlay positions ==============
// Updated every frame by GameCore, read by MapPanel — kept out of Zustand so
// per-frame position updates don't trigger React re-renders.

export interface MapMarker {
  x: number;
  z: number;
  rotation: number;
  color: string;
}

const mapPlayerPosition: MapMarker = { x: 0, z: 0, rotation: 0, color: '#45b7d1' };
let mapOtherPlayers: MapMarker[] = [];

export function updateMapPlayerPosition(x: number, z: number, rotation: number, color: string): void {
  mapPlayerPosition.x = x;
  mapPlayerPosition.z = z;
  mapPlayerPosition.rotation = rotation;
  mapPlayerPosition.color = color;
}

export function getMapPlayerPosition(): MapMarker {
  return mapPlayerPosition;
}

export function updateMapOtherPlayers(players: MapMarker[]): void {
  mapOtherPlayers = players;
}

export function getMapOtherPlayers(): MapMarker[] {
  return mapOtherPlayers;
}

// ============== Debug-stats rate limiter (10 Hz) ==============

let lastDebugStatsTime = 0;
const DEBUG_STATS_INTERVAL_MS = 100;

/** Push FPS / tick timing to the store at most 10×/second. */
export function updateDebugStats(fps: number, tickMs: number): void {
  const now = performance.now();
  if (now - lastDebugStatsTime >= DEBUG_STATS_INTERVAL_MS) {
    useGameStore.getState().setDebugStats(fps, tickMs);
    lastDebugStatsTime = now;
  }
}

// ============== Physics HUD (per-frame, never into React) ==============
// Written every frame by GameCore, read by the debug panel during its own 5 Hz re-render.
// Deliberately NOT Zustand state: per-frame writes there would re-render the UI tree.

export interface PhysicsHud {
  /** Physics is suspended: chunks required to simulate the next tick aren't loaded. */
  frozen: boolean;
  /** Horizontal motion was dropped this tick because it led into unknown chunks. */
  blockedByUnknown: boolean;
  grounded: boolean;
  /** Depenetration ladder exhausted — the game layer should respawn. */
  stuck: boolean;
  steppedUp: boolean;
  hitCeiling: boolean;
  wallContact: boolean;
  /** Ticks in which the interior verifier failed and the ladder ran (cumulative). */
  depenetrations: number;
  /** Seconds of continuous falling (frozen ticks excluded). */
  fallTime: number;
  velocityY: number;

  // ---- Per-frame field probes (GameCore, inside the physics window) ----
  // Read-only today: the panel shows them, and the swimming work reads submersion from the step.

  /** Material id of the solid the player stands on, or -1 (air / unloaded). */
  groundMaterial: number;
  /** Material id the punch ray would hit at reach, or -1 (nothing / unloaded chunk). */
  targetMaterial: number;
  /** Fraction of the capsule below the liquid surface, 0..1. */
  submersionFraction: number;
  feetInLiquid: boolean;
  eyeInLiquid: boolean;
  /** Material id of the liquid at the capsule, or -1 when dry. */
  liquidMaterial: number;
}

const physicsHud: PhysicsHud = {
  frozen: false, blockedByUnknown: false, grounded: false, stuck: false,
  steppedUp: false, hitCeiling: false, wallContact: false,
  depenetrations: 0, fallTime: 0, velocityY: 0,
  groundMaterial: -1, targetMaterial: -1,
  submersionFraction: 0, feetInLiquid: false, eyeInLiquid: false, liquidMaterial: -1,
};

export function updatePhysicsHud(next: Omit<PhysicsHud, 'depenetrations'> & { depenetrations: number }): void {
  physicsHud.frozen = next.frozen;
  physicsHud.blockedByUnknown = next.blockedByUnknown;
  physicsHud.grounded = next.grounded;
  physicsHud.stuck = next.stuck;
  physicsHud.steppedUp = next.steppedUp;
  physicsHud.hitCeiling = next.hitCeiling;
  physicsHud.wallContact = next.wallContact;
  physicsHud.depenetrations += next.depenetrations;
  physicsHud.fallTime = next.fallTime;
  physicsHud.velocityY = next.velocityY;
  physicsHud.groundMaterial = next.groundMaterial;
  physicsHud.targetMaterial = next.targetMaterial;
  physicsHud.submersionFraction = next.submersionFraction;
  physicsHud.feetInLiquid = next.feetInLiquid;
  physicsHud.eyeInLiquid = next.eyeInLiquid;
  physicsHud.liquidMaterial = next.liquidMaterial;
}

export function getPhysicsHud(): Readonly<PhysicsHud> {
  return physicsHud;
}

// ============== Chunk-clear registry ==============
// GameCore registers the callback; the debug panel invokes it (F9).

let clearChunksCallback: (() => void) | null = null;

export function setClearChunksCallback(callback: () => void): void {
  clearChunksCallback = callback;
}

export function clearAndReloadChunks(): void {
  if (clearChunksCallback) {
    clearChunksCallback();
  } else {
    console.warn('[transient] No chunk clear callback registered');
  }
}
